import { useState } from 'react';

export default function SponsorshipForm() {
  const [formData, setFormData] = useState({
    businessName: '',
    contactName: '',
    email: '',
    phone: '',
    sponsorshipLevel: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams({
        businessName: formData.businessName,
        contactName: formData.contactName,
        email: formData.email,
        phone: formData.phone,
        sponsorshipLevel: formData.sponsorshipLevel
      }).toString();

      const response = await fetch('https://readdy.ai/api/form/d6lh25iqoe30lj0v7vug', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          businessName: '',
          contactName: '',
          email: '',
          phone: '',
          sponsorshipLevel: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="sponsorship-form" className="py-24 bg-gray-50">
      <div className="max-w-3xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-5xl md:text-6xl font-black text-gray-900 mb-6">
            Become A <span className="text-purple-700">CYFA Sponsor</span>
          </h2>
          <p className="text-xl text-gray-700 font-medium">
            Fill out the form below and a CYFA representative will follow up with details
          </p>
        </div>

        <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12">
          <form id="cyfa-sponsorship-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="businessName" className="block text-lg font-bold text-gray-900 mb-3">
                Business Name *
              </label>
              <input
                type="text"
                id="businessName"
                name="businessName"
                value={formData.businessName}
                onChange={(e) => setFormData({ ...formData, businessName: e.target.value })}
                required
                className="w-full px-6 py-4 text-lg border-2 border-gray-300 rounded-xl focus:border-purple-700 focus:outline-none transition-colors"
                placeholder="Your Business Name"
              />
            </div>

            <div>
              <label htmlFor="contactName" className="block text-lg font-bold text-gray-900 mb-3">
                Contact Name *
              </label>
              <input
                type="text"
                id="contactName"
                name="contactName"
                value={formData.contactName}
                onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
                required
                className="w-full px-6 py-4 text-lg border-2 border-gray-300 rounded-xl focus:border-purple-700 focus:outline-none transition-colors"
                placeholder="Your Name"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-lg font-bold text-gray-900 mb-3">
                Email *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="w-full px-6 py-4 text-lg border-2 border-gray-300 rounded-xl focus:border-purple-700 focus:outline-none transition-colors"
                placeholder="your@email.com"
              />
            </div>

            <div>
              <label htmlFor="phone" className="block text-lg font-bold text-gray-900 mb-3">
                Phone *
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                required
                className="w-full px-6 py-4 text-lg border-2 border-gray-300 rounded-xl focus:border-purple-700 focus:outline-none transition-colors"
                placeholder="(555) 123-4567"
              />
            </div>

            <div>
              <label htmlFor="sponsorshipLevel" className="block text-lg font-bold text-gray-900 mb-3">
                Sponsorship Level *
              </label>
              <select
                id="sponsorshipLevel"
                name="sponsorshipLevel"
                value={formData.sponsorshipLevel}
                onChange={(e) => setFormData({ ...formData, sponsorshipLevel: e.target.value })}
                required
                className="w-full px-6 py-4 text-lg border-2 border-gray-300 rounded-xl focus:border-purple-700 focus:outline-none transition-colors cursor-pointer"
              >
                <option value="">Select a sponsorship level</option>
                <option value="Helmet Sponsor - $4,000">Helmet Sponsor - $4,000</option>
                <option value="Super Bowl Sponsor - $3,000">Super Bowl Sponsor - $3,000</option>
                <option value="Touchdown Sponsor - $2,000">Touchdown Sponsor - $2,000</option>
                <option value="Field Goal Sponsor - $1,000">Field Goal Sponsor - $1,000</option>
                <option value="Extra Point Sponsor - $500">Extra Point Sponsor - $500</option>
              </select>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full px-4 py-4 sm:px-8 sm:py-5 bg-purple-700 hover:bg-purple-800 disabled:bg-gray-400 text-white text-base sm:text-xl font-black rounded-xl transition-all duration-300 transform hover:scale-105 shadow-xl whitespace-nowrap cursor-pointer"
            >
              {isSubmitting ? 'Submitting...' : 'Request Sponsorship Info'}
            </button>

            {submitStatus === 'success' && (
              <div className="bg-green-50 border-2 border-green-500 rounded-xl p-6 text-center">
                <i className="ri-check-line text-4xl text-green-600 mb-2"></i>
                <p className="text-lg font-bold text-green-800">
                  Thank you! A CYFA representative will contact you soon.
                </p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="bg-red-50 border-2 border-red-500 rounded-xl p-6 text-center">
                <i className="ri-error-warning-line text-4xl text-red-600 mb-2"></i>
                <p className="text-lg font-bold text-red-800">
                  Something went wrong. Please try again.
                </p>
              </div>
            )}
          </form>

          <p className="text-center text-gray-600 mt-8 text-base">
            A CYFA representative will follow up with details
          </p>
        </div>
      </div>
    </section>
  );
}